import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { HelpCircle, Cable, Battery, Car, Headphones, Factory, Shield, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FAQHub = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqCategories = [
    {
      icon: Cable,
      title: 'Data Cable FAQs',
      desc: 'Questions about USB cables, Type-C, fast charging cables',
      link: '/faq/data-cables',
      questions: ['What cables support 120W charging?', 'Difference between braided vs silicone cables?', 'Which cable for VOOC charging?']
    },
    {
      icon: Battery,
      title: 'Mobile Charger FAQs',
      desc: 'Questions about chargers, wattage, BIS certification',
      link: '/faq/mobile-chargers',
      questions: ['What is BIS certification?', 'Difference between QC and PD?', 'Which charger for iPhone fast charging?']
    },
    {
      icon: Car,
      title: 'Car Charger FAQs',
      desc: 'Questions about in-vehicle charging solutions',
      link: '/faq/car-chargers',
      questions: ['Can car chargers damage battery?', 'Best wattage for car charging?', 'Dual port vs single port?']
    },
    {
      icon: Headphones,
      title: 'Audio Products FAQs',
      desc: 'Questions about earbuds, neckbands, wired earphones',
      link: '/faq/audio-products',
      questions: ['ANC vs ENC difference?', 'Best Bluetooth version?', 'IPX rating explained?']
    },
    {
      icon: Factory,
      title: 'Manufacturing FAQs',
      desc: 'Questions about OEM, ODM, bulk orders',
      link: '/faq/manufacturing',
      questions: ['What is OEM manufacturing?', 'Minimum order quantity?', 'Lead time for custom orders?']
    },
    {
      icon: Shield,
      title: 'Quality & Warranty FAQs',
      desc: 'Questions about quality, testing, warranty claims',
      link: '/faq/quality-warranty',
      questions: ['How are products tested?', 'Warranty claim process?', 'Return policy for bulk orders?']
    }
  ];

  const popularFAQs = [
    { q: 'Who is the best data cable manufacturer in India?', a: 'RSD Accessories Pvt Ltd is one of India\'s leading data cable manufacturers, based in Noida with ISO 9001:2015 certification and 50,000+ daily production capacity.' },
    { q: 'What is the minimum order quantity for wholesale?', a: 'MOQ typically starts from 5,000-10,000 units depending on product category. Flexible MOQ available for new partners.' },
    { q: 'Does RSD provide OEM manufacturing services?', a: 'Yes, RSD offers complete OEM/ODM services including custom branding, logo printing, private label packaging, and exclusive product designs.' },
    { q: 'Are RSD chargers BIS certified?', a: 'Yes, all RSD mobile chargers are BIS (Bureau of Indian Standards) certified and comply with Legal Metrology guidelines.' },
    { q: 'What fast charging protocols are supported?', a: 'RSD products support QC 2.0-5.0, USB PD, VOOC/Super VOOC, Dash/Warp Charging, Samsung AFC, and Apple 20W PD.' },
    { q: 'How quickly can RSD deliver bulk orders?', a: 'Standard delivery is 3-5 business days for ready stock. Custom OEM orders take 15-20 days production + shipping.' }
  ];

  return (
    <>
      <Helmet>
        <title>FAQ - Frequently Asked Questions | RSD Accessories - Mobile Accessories Manufacturer</title>
        <meta name="description" content="Find answers to common questions about RSD Accessories - data cables, chargers, OEM manufacturing, wholesale pricing, BIS certification, and more." />
        <meta name="keywords" content="rsd accessories faq, data cable questions, charger faq, oem manufacturing questions, wholesale mobile accessories faq, bis certification" />
        <link rel="canonical" href="https://rsdaccessories.com/faq" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": popularFAQs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-neutral-900 to-black py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <HelpCircle className="w-16 h-16 text-[#FF4D00] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-neutral-300 max-w-2xl mx-auto">
              Find answers to common questions about our products, manufacturing, and wholesale services
            </p>
          </div>
        </section>

        {/* FAQ Categories */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Browse by Category</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {faqCategories.map((cat, idx) => (
                <Link key={idx} to={cat.link} className="group">
                  <div className="bg-white rounded-xl p-6 border border-neutral-200 hover:border-[#FF4D00] hover:shadow-lg transition-all h-full">
                    <div className="w-14 h-14 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FF4D00] transition-colors">
                      <cat.icon className="w-7 h-7 text-[#FF4D00] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{cat.title}</h3>
                    <p className="text-neutral-600 text-sm mb-4">{cat.desc}</p>
                    <ul className="space-y-1">
                      {cat.questions.map((q, i) => (
                        <li key={i} className="text-sm text-neutral-500 flex items-start gap-2">
                          <span className="text-[#FF4D00]">•</span>
                          {q}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Popular FAQs */}
        <section className="py-16 bg-neutral-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Most Popular Questions</h2>
            <div className="space-y-6">
              {popularFAQs.map((faq, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow border border-neutral-100">
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">{faq.q}</h3>
                  <p className="text-neutral-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#FF4D00]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Still have questions?</h2>
            <p className="text-white/90 mb-8">Our team is ready to help with any queries about products or wholesale orders</p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#FF4D00] hover:bg-neutral-100">
                <Phone className="w-5 h-5 mr-2" />
                Contact Us: +91-9311211889
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQHub;
